export interface TriggerSettings {
  phoneNumber?: string;
  email?: string;
  customer_first_name?: string;
  customer_last_name?: string;
  drop_off_date_formatted?: string;
  drop_off_time_formatted?: string;
  business_name?: string;
  appointment_id?: string;
  service_name?: string;
  reminderHours?: number[]; // Hours before appointment to send reminders
  statusValues?: string[]; // Status values for the booking status trigger
}

export interface Trigger {
  id: string;
  name: string;
  description: string;
  settings?: TriggerSettings;
  execute: () => Promise<void | TriggerSettings>;
}

export const TEST_TRIGGER: Trigger = {
  id: 'test_trigger',
  name: 'Test Trigger',
  description: 'A simple trigger for testing automations',
  settings: {
    phoneNumber: '',
    email: '',
    customer_first_name: '',
    customer_last_name: '',
    business_name: ''
  },
  execute: async () => {
    console.log('Test trigger executed!');
    return {
      phoneNumber: '555-123-4567',
      email: 'customer@example.com',
      customer_first_name: 'John',
      customer_last_name: 'Doe',
      business_name: 'X Detail Studio',
      drop_off_date_formatted: 'March 25, 2023',
      drop_off_time_formatted: '10:30 AM',
      appointment_id: 'APPT-12345',
      service_name: 'Full Detail'
    };
  }
};

export const SCHEDULED_TRIGGER: Trigger = {
  id: 'scheduled_trigger',
  name: 'Appointment Scheduled',
  description: 'Triggers when new bookings are created',
  execute: async () => {
    console.log('Scheduled trigger executed!');
    return {
      phoneNumber: '555-123-4567',
      email: 'customer@example.com',
      customer_first_name: 'John',
      customer_last_name: 'Doe',
      business_name: 'X Detail Studio',
      drop_off_date_formatted: 'March 25, 2023',
      drop_off_time_formatted: '10:30 AM',
      appointment_id: 'APPT-12345',
      service_name: 'Full Detail'
    };
  }
};

export const APPOINTMENT_REMINDER: Trigger = {
  id: 'appointment_reminder',
  name: 'Appointment Reminder',
  description: 'Sends a reminder before the appointment date',
  settings: {
    reminderHours: [24] // Default to 24 hours
  },
  execute: async () => {
    console.log('Appointment reminder trigger executed!');
    return {
      phoneNumber: '555-123-4567',
      email: 'customer@example.com',
      customer_first_name: 'John',
      customer_last_name: 'Doe',
      business_name: 'X Detail Studio',
      drop_off_date_formatted: 'March 25, 2023',
      drop_off_time_formatted: '10:30 AM',
      appointment_id: 'APPT-12345',
      service_name: 'Full Detail'
    };
  }
};

export const APPOINTMENT_CANCELED: Trigger = {
  id: 'appointment_canceled',
  name: 'Appointment Canceled',
  description: 'Triggers when an appointment is canceled',
  execute: async () => {
    console.log('Appointment canceled trigger executed!');
    return {
      phoneNumber: '555-123-4567',
      email: 'customer@example.com',
      customer_first_name: 'John',
      customer_last_name: 'Doe',
      business_name: 'X Detail Studio',
      drop_off_date_formatted: 'March 25, 2023',
      drop_off_time_formatted: '10:30 AM',
      appointment_id: 'APPT-12345',
      service_name: 'Full Detail'
    };
  }
};

export const BOOKING_STATUS_TRIGGER: Trigger = {
  id: 'booking_status',
  name: 'Booking Status',
  description: 'Triggers when a booking status changes',
  settings: {
    statusValues: ['in_progress', 'completed']
  },
  execute: async () => {
    console.log('Booking status trigger executed!');
    return {
      phoneNumber: '555-123-4567',
      email: 'customer@example.com',
      customer_first_name: 'John',
      customer_last_name: 'Doe',
      customer_full_name: 'John Doe',
      business_name: 'X Detail Studio',
      drop_off_date_formatted: 'March 25, 2023',
      drop_off_time_formatted: '10:30 AM',
      start_date_formatted: 'March 25, 2023',
      start_time_formatted: '10:30 AM',
      appointment_id: 'APPT-12345',
      booking_id: 'APPT-12345',
      booking_number: '12345',
      service_name: 'Full Detail',
      status: 'in_progress',
      status_formatted: 'In Progress',
      status_date: 'January 1, 2023',
      status_time: '9:00 AM'
    };
  }
};

export const AVAILABLE_TRIGGERS = [SCHEDULED_TRIGGER, APPOINTMENT_REMINDER, APPOINTMENT_CANCELED, BOOKING_STATUS_TRIGGER]; 